/**
 * useOrgPickers — list hooks the CreateTaskPage form needs.
 *
 * Each is a thin SELECT through the authenticated client; RLS scopes
 * to the caller's org. Cached at the React Query layer so the form
 * doesn't re-fetch on every keystroke.
 *
 * Kept together because they're all "lists for the create-task form"
 * — one hook per concern would proliferate files for what is really
 * one concern: filling pickers on this page.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'

export interface RoomPickerRow {
  id:             string
  room_number:    string
  building:       { id: string; name: string } | null
  floor:          { id: string; label: string } | null
}

export interface CleanerPickerRow {
  id:        string
  full_name: string
  role:      string
}

export interface CampusPickerRow {
  id:   string
  name: string
}

export interface BuildingPickerRow {
  id:           string
  name:         string
  campus_id:    string
  floor_count:  number | null
}

export interface BuildingRoomRow {
  id:             string
  room_number:    string
  bed_count:      number
  bathroom_count: number
}

const FIVE_MIN = 5 * 60 * 1000

export function useOrgRooms(): UseQueryResult<RoomPickerRow[]> {
  const supabase = useSupabase()
  return useQuery({
    queryKey: ['org-rooms'],
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<RoomPickerRow[]> => {
      const { data, error } = await supabase
        .from('rooms')
        .select('id, room_number, building:buildings(id, name), floor:floors(id, label)')
        .eq('is_active', true)
        .order('room_number', { ascending: true })
        .limit(2000)
      if (error) throw error
      return (data ?? []) as unknown as RoomPickerRow[]
    },
  })
}

export function useOrgCampuses(): UseQueryResult<CampusPickerRow[]> {
  const supabase = useSupabase()
  return useQuery({
    queryKey: ['org-campuses'],
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<CampusPickerRow[]> => {
      const { data, error } = await supabase
        .from('campuses')
        .select('id, name')
        .eq('is_active', true)
        .order('name', { ascending: true })
        .limit(200)
      if (error) throw error
      return (data ?? []) as unknown as CampusPickerRow[]
    },
  })
}

export function useOrgBuildings(): UseQueryResult<BuildingPickerRow[]> {
  const supabase = useSupabase()
  return useQuery({
    queryKey: ['org-buildings'],
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<BuildingPickerRow[]> => {
      const { data, error } = await supabase
        .from('buildings')
        .select('id, name, campus_id, floor_count')
        .eq('is_active', true)
        .order('name', { ascending: true })
        .limit(500)
      if (error) throw error
      return (data ?? []) as unknown as BuildingPickerRow[]
    },
  })
}

/**
 * Existing rooms in one building. Powers the room-discovery picker:
 * the dropdown shows every plausible 1xx..Nxx number for the building's
 * floor_count, and existing rooms pre-fill bed/bath when the cleaner
 * picks them. Lazy — fetches only after a building has been selected.
 */
export function useBuildingRooms(buildingId: string | null): UseQueryResult<BuildingRoomRow[]> {
  const supabase = useSupabase()
  return useQuery({
    queryKey: ['building-rooms', buildingId],
    enabled: !!buildingId,
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<BuildingRoomRow[]> => {
      // Audit F15: `enabled: !!buildingId` already gates this so the
      // queryFn never runs with a null id, but TypeScript can't infer
      // that. Guard explicitly instead of casting `as string`.
      if (!buildingId) return []
      const { data, error } = await supabase
        .from('rooms')
        .select('id, room_number, bed_count, bathroom_count')
        .eq('building_id', buildingId)
        .eq('is_active', true)
        .order('room_number', { ascending: true })
        .limit(2000)
      if (error) throw error
      return (data ?? []) as unknown as BuildingRoomRow[]
    },
  })
}

export function useOrgCleaners(): UseQueryResult<CleanerPickerRow[]> {
  const supabase = useSupabase()
  return useQuery({
    queryKey: ['org-cleaners'],
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<CleanerPickerRow[]> => {
      // Anyone the supervisor might want to hand a task to. Includes
      // both cleaners and supervisors (supervisors do work too,
      // especially in a small contractor like Above and Beyond Clean).
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, role')
        .in('role', ['cleaner', 'supervisor'])
        .eq('is_active', true)
        .order('full_name', { ascending: true })
        .limit(500)
      if (error) throw error
      return (data ?? []) as unknown as CleanerPickerRow[]
    },
  })
}
